import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth, portalFor } from "@/lib/auth";

import Login from "@/pages/Login";

import BuyerLayout from "@/layouts/BuyerLayout";
import BuyerHome from "@/pages/buyer/Home";
import BuyerFinancials from "@/pages/buyer/Financials";
import BuyerProgress from "@/pages/buyer/Progress";
import BuyerDocuments from "@/pages/buyer/Documents";
import BuyerTickets from "@/pages/buyer/Tickets";
import BuyerTicketDetail from "@/pages/buyer/TicketDetail";
import BuyerTicketNew from "@/pages/buyer/TicketNew";
import BuyerHandover from "@/pages/buyer/Handover";

import StaffLayout from "@/layouts/StaffLayout";
import CRMDashboard from "@/pages/crm/Dashboard";
import CRMCustomers from "@/pages/crm/Customers";
import CRMCustomerDetail from "@/pages/crm/CustomerDetail";
import CRMTickets from "@/pages/crm/Tickets";
import CRMTicketDetail from "@/pages/crm/TicketDetail";
import CRMConstruction from "@/pages/crm/Construction";
import CRMDocuments from "@/pages/crm/Documents";

import MgmtDashboard from "@/pages/mgmt/Dashboard";
import MgmtProjects from "@/pages/mgmt/Projects";
import MgmtProjectDetail from "@/pages/mgmt/ProjectDetail";
import MgmtCustomers from "@/pages/mgmt/Customers";
import MgmtEmployees from "@/pages/mgmt/Employees";
import MgmtEscalations from "@/pages/mgmt/Escalations";
import MgmtCollections from "@/pages/mgmt/Collections";

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="rc-skeleton" style={{ width: 120, height: 120, borderRadius: 60 }} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={portalFor(user.role)} replace />;
};

const Protected = ({ roles }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={portalFor(user.role)} replace />;
  return <Outlet />;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />

          {/* Buyer */}
          <Route element={<Protected roles={["buyer"]} />}>
            <Route element={<BuyerLayout />}>
              <Route path="/buyer/home" element={<BuyerHome />} />
              <Route path="/buyer/financials" element={<BuyerFinancials />} />
              <Route path="/buyer/progress" element={<BuyerProgress />} />
              <Route path="/buyer/documents" element={<BuyerDocuments />} />
              <Route path="/buyer/tickets" element={<BuyerTickets />} />
              <Route path="/buyer/tickets/new" element={<BuyerTicketNew />} />
              <Route path="/buyer/tickets/:id" element={<BuyerTicketDetail />} />
              <Route path="/buyer/handover" element={<BuyerHandover />} />
            </Route>
          </Route>

          {/* CRM */}
          <Route element={<Protected roles={["crm_executive", "crm_manager", "management"]} />}>
            <Route element={<StaffLayout portal="crm" />}>
              <Route path="/crm/dashboard" element={<CRMDashboard />} />
              <Route path="/crm/customers" element={<CRMCustomers />} />
              <Route path="/crm/customers/:id" element={<CRMCustomerDetail />} />
              <Route path="/crm/tickets" element={<CRMTickets />} />
              <Route path="/crm/tickets/:id" element={<CRMTicketDetail />} />
              <Route path="/crm/construction" element={<CRMConstruction />} />
              <Route path="/crm/documents" element={<CRMDocuments />} />
            </Route>
          </Route>

          {/* Management */}
          <Route element={<Protected roles={["crm_manager", "management"]} />}>
            <Route element={<StaffLayout portal="management" />}>
              <Route path="/management/dashboard" element={<MgmtDashboard />} />
              <Route path="/management/projects" element={<MgmtProjects />} />
              <Route path="/management/projects/:id" element={<MgmtProjectDetail />} />
              <Route path="/management/customers" element={<MgmtCustomers />} />
              <Route path="/management/employees" element={<MgmtEmployees />} />
              <Route path="/management/escalations" element={<MgmtEscalations />} />
              <Route path="/management/collections" element={<MgmtCollections />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
